// Landing page at "/" (redesign direction C, "Atlas").
//
//   hero    a headline, a one-line pitch and the Ask box (hands the question to
//           /chat?q=…) with example queries, next to a live preview of the atlas
//   stats   corpus totals from the knowledge graph (+ atlas IOC count)
//   boards  rising families / actors and a compact by-country view, all from
//           the nightly trends "pulse" snapshot
import { useId, useLayoutEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { fetchAtlasCore } from '../atlas/atlasShared';
import type { AtlasCore } from '../atlas/atlasShared';
import { fetchTopReferenced } from '../../lib/exploreApi';
import { fetchPulse } from '../trends/api';
import type { PulseDimension, PulsePayload, PulseTile } from '../trends/types';
import { entityHref } from '../trends/util';
import HeroStage from './HeroStage';

import './landing.css';

// ── Ask box ─────────────────────────────────────────────────────────────

const EXAMPLES: { label: string; kind: 'actor' | 'family' | 'technique' | 'cve'; query: string }[] = [
  {
    label: 'APT29',
    kind: 'actor',
    query: 'Which initial access techniques has APT29 used in its campaigns since 2023?',
  },
  {
    label: 'win.lumma',
    kind: 'family',
    query: 'What C2 infrastructure and delivery chains are reported for Lumma Stealer?',
  },
  {
    label: 'T1566.001',
    kind: 'technique',
    query: 'Which threat actors use spearphishing attachments (T1566.001) to deliver loaders?',
  },
  {
    label: 'CVE-2023-4966',
    kind: 'cve',
    query: 'Which actors exploited Citrix Bleed (CVE-2023-4966), and what did they deploy afterwards?',
  },
];

function AskBox() {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const hintId = useId();

  // Grow with the text (example questions wrap to two or three lines).
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
  }, [text]);

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    const q = text.trim();
    if (!q) {
      inputRef.current?.focus();
      return;
    }
    navigate(`/chat?q=${encodeURIComponent(q)}`);
  };

  return (
    <div className="landing-ask-wrap">
      <form className="landing-ask" onSubmit={submit} role="search">
        <label className="landing-sr-only" htmlFor={`${hintId}-q`}>
          Ask a question about the threat-intel corpus
        </label>
        <textarea
          id={`${hintId}-q`}
          ref={inputRef}
          className="landing-ask-input"
          rows={1}
          maxLength={2000}
          value={text}
          placeholder="Ask about an actor, a malware family, an IOC or a CVE…"
          aria-describedby={hintId}
          enterKeyHint="send"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <button type="submit" className="landing-ask-send">
          Ask
          <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
            <path d="M5 12h13M13 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </form>
      <p id={hintId} className="landing-ask-hint">
        Opens a new chat. Answers cite their sources.
      </p>
      <div className="landing-examples" role="group" aria-label="Example questions">
        {EXAMPLES.map((ex) => (
          <button
            key={ex.label}
            type="button"
            className="landing-example"
            title={ex.query}
            onClick={() => {
              setText(ex.query);
              requestAnimationFrame(() => {
                const el = inputRef.current;
                if (!el) return;
                el.focus();
                el.setSelectionRange(ex.query.length, ex.query.length);
              });
            }}
          >
            <i className={`landing-dot landing-dot-${ex.kind}`} aria-hidden="true" />
            {ex.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Hero ────────────────────────────────────────────────────────────────

function Hero({ core, coreState }: { core: AtlasCore | null | undefined; coreState: CoreState }) {
  const caption =
    coreState === 'ready' && core
      ? `${fmt(core.entities.length)} families & actors · ${fmt(core.clusters.length)} islands`
      : coreState === 'loading'
        ? 'Charting the atlas…'
        : coreState === 'missing'
          ? 'The atlas is built nightly. It appears here after the first build.'
          : 'The atlas could not be loaded right now.';

  return (
    <section className="landing-hero" aria-labelledby="landing-title">
      <div className="landing-hero-copy">
        <span className="landing-eyebrow">malpedia · RAG</span>
        <h1 id="landing-title" className="landing-title">
          Every Malpedia report, one question away.
        </h1>
        <p className="landing-pitch">
          Ask about an actor, a malware family, an indicator or a CVE and get an answer with every
          source cited.
        </p>
        <AskBox />
      </div>
      <div className="landing-hero-map">
        <HeroStage core={core} coreState={coreState} />
        <div className="landing-hero-foot">
          <span className="landing-hero-caption" aria-live="polite">{caption}</span>
          <Link to="/overview" className="landing-atlas-link">
            Open the atlas <span aria-hidden="true">→</span>
          </Link>
        </div>
      </div>
    </section>
  );
}

// ── Stat tiles ──────────────────────────────────────────────────────────

const IOC_KEY = /^total_(hash|ip|domain|url|email|filepath|registry|pdb|onion|cidr|crypto\..+)$/;

function StatTiles({
  core,
  coreState,
  builtAt,
}: {
  core: AtlasCore | null | undefined;
  coreState: CoreState;
  builtAt: string | null;
}) {
  const q = useQuery({
    queryKey: ['landing', 'explore-stats'],
    queryFn: async () => {
      const res = await fetchTopReferenced({ lists: false });
      if (!res.stats || Object.keys(res.stats).length === 0) throw new Error('no stats');
      return res.stats;
    },
    staleTime: 10 * 60_000,
  });
  const s = q.data;

  const iocFromAtlas = coreState === 'ready' && core ? core.iocTotal : null;
  const iocIndexed = s
    ? Object.entries(s).reduce((sum, [k, v]) => (IOC_KEY.test(k) ? sum + (v || 0) : sum), 0)
    : null;

  const tiles: { label: string; value: number | null | undefined; hint: string; pending: boolean }[] = [
    { label: 'Reports indexed', value: s?.total_documents, hint: 'blogs, papers & vendor reports', pending: q.isLoading },
    { label: 'Malware families', value: s?.total_families, hint: 'from the Malpedia inventory', pending: q.isLoading },
    {
      label: 'Threat actors',
      value: s?.total_actors,
      hint: s?.total_countries ? `attributed to ${fmt(s.total_countries)} countries` : 'with aliases & attribution',
      pending: q.isLoading,
    },
    iocFromAtlas != null
      ? { label: 'Attributed IOCs', value: iocFromAtlas, hint: 'linked to a family or actor', pending: false }
      : {
          label: 'Indicators indexed',
          value: iocIndexed,
          hint: 'extracted from the reports',
          pending: q.isLoading || coreState === 'loading',
        },
  ];

  const updated = [
    core?.builtAt ? `atlas built ${relTime(core.builtAt)}` : null,
    builtAt ? `trends snapshot ${relTime(builtAt)}` : null,
  ].filter(Boolean);

  return (
    <section className="landing-stats-wrap" aria-label="Corpus at a glance">
      <div className="landing-stats">
        {tiles.map((t) => (
          <div key={t.label} className="landing-stat">
            <span className="landing-stat-label">{t.label}</span>
            {t.pending ? (
              <span className="landing-skel landing-skel-num" role="status" aria-label="Loading" />
            ) : (
              <b className="landing-stat-value">{t.value != null ? fmtCompact(t.value) : '—'}</b>
            )}
            <em className="landing-stat-hint">{t.hint}</em>
          </div>
        ))}
      </div>
      {q.isError ? (
        <p className="landing-updated landing-updated-warn">
          Corpus counts are unavailable right now.{' '}
          <button type="button" className="landing-textbtn" onClick={() => void q.refetch()}>
            Try again
          </button>
        </p>
      ) : (
        updated.length > 0 && <p className="landing-updated">Live counts from the knowledge graph · {updated.join(' · ')}</p>
      )}
    </section>
  );
}

// ── Trend boards ────────────────────────────────────────────────────────

type BoardRange = '6m' | '1y' | '2y';
const RANGES: { value: BoardRange; label: string; long: string }[] = [
  { value: '6m', label: '6 mo', long: '6 months' },
  { value: '1y', label: '1 yr', long: '12 months' },
  { value: '2y', label: '2 yr', long: '2 years' },
];

function usePulse(range: BoardRange, dim: PulseDimension) {
  return useQuery({
    queryKey: ['trends', 'pulse', range, dim],
    queryFn: () => fetchPulse(range, dim),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

/** Rising first: biggest change vs the prior period on top, new entries (no baseline) above all. */
function byDelta(tiles: PulseTile[]): PulseTile[] {
  return [...tiles].sort((a, b) => (b.delta_pct ?? Infinity) - (a.delta_pct ?? Infinity) || b.total - a.total);
}

function Delta({ tile }: { tile: PulseTile }) {
  const d = tile.delta_pct;
  if (d == null) {
    return <span className="landing-delta landing-delta-new" title="No reports in the prior period">new</span>;
  }
  const up = d > 0;
  return (
    <span
      className={`landing-delta ${up ? 'landing-delta-up' : 'landing-delta-down'}`}
      title={`${fmt(tile.total)} reports vs ${fmt(tile.prior_total)} in the prior period`}
    >
      {up ? '+' : d < 0 ? '−' : '±'}
      {Math.abs(d).toFixed(0)}%
    </span>
  );
}

function MiniSpark({ data }: { data: Array<{ t: string; n: number }> }) {
  if (data.length < 2) return <span className="landing-spark landing-spark-empty" />;
  const w = 64;
  const h = 20;
  const max = Math.max(1, ...data.map((p) => p.n));
  const pts = data.map((p, i) => [(i / (data.length - 1)) * w, h - 1 - (p.n / max) * (h - 3)]);
  const line = pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  return (
    <svg className="landing-spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
      <polygon points={`0,${h} ${line} ${w},${h}`} className="landing-spark-area" />
      <polyline points={line} className="landing-spark-line" />
    </svg>
  );
}

function BoardState({
  q,
  rows,
}: {
  q: { isLoading: boolean; isError: boolean; data?: PulsePayload; refetch: () => unknown };
  rows: number;
}) {
  if (q.isLoading) {
    return (
      <div className="landing-board-skel" role="status" aria-label="Loading">
        {Array.from({ length: rows }, (_, i) => (
          <span key={i} className="landing-skel landing-skel-row" style={{ animationDelay: `${i * 80}ms` }} />
        ))}
      </div>
    );
  }
  if (q.isError) {
    return (
      <div className="landing-board-msg">
        <strong>Couldn’t load the trends snapshot.</strong>
        <button type="button" className="landing-textbtn" onClick={() => void q.refetch()}>
          Try again
        </button>
      </div>
    );
  }
  return (
    <div className="landing-board-msg">
      <strong>Nothing to show yet.</strong>
      <span>The trends snapshot is rebuilt every night.</span>
    </div>
  );
}

function RisingBoard({ range, dim, title }: { range: BoardRange; dim: 'family' | 'actor'; title: string }) {
  const q = usePulse(range, dim);
  const tiles = byDelta(q.data?.tiles ?? []);
  const label = dim === 'actor' ? 'ThreatActor' : 'MalwareFamily';

  return (
    <article className="landing-board">
      <h2 className="landing-board-title">{title}</h2>
      {tiles.length === 0 ? (
        <BoardState q={q} rows={6} />
      ) : (
        <ol className="landing-rows">
          {tiles.map((t, i) => {
            const href = entityHref(label, t.name);
            return (
              <li key={t.id} className="landing-row">
                <span className="landing-row-rank">{i + 1}</span>
                <span className="landing-row-name">
                  {href ? <Link to={href}>{t.name}</Link> : t.name}
                </span>
                <MiniSpark data={t.series} />
                <Delta tile={t} />
                <span className="landing-row-count" title="Reports in this period">{fmtCompact(t.total)}</span>
              </li>
            );
          })}
        </ol>
      )}
    </article>
  );
}

function CountryBoard({ range }: { range: BoardRange }) {
  const q = usePulse(range, 'country');
  const tiles = q.data?.tiles ?? [];
  const max = Math.max(1, ...tiles.map((t) => t.total));

  return (
    <article className="landing-board">
      <h2 className="landing-board-title">By attributed country</h2>
      {tiles.length === 0 ? (
        <BoardState q={q} rows={6} />
      ) : (
        <ol className="landing-rows">
          {tiles.map((t) => {
            const known = t.id !== '∅' && /^[A-Za-z]{2}$/.test(t.id);
            return (
              <li key={t.id} className="landing-row landing-row-country">
                <span className="landing-row-flag" aria-hidden="true">{t.flag || '·'}</span>
                <span className="landing-row-name">
                  {known ? <Link to={`/country/${encodeURIComponent(t.id.toUpperCase())}`}>{t.name}</Link> : t.name}
                </span>
                <span className="landing-bar" aria-hidden="true">
                  <span style={{ width: `${(t.total / max) * 100}%` }} />
                </span>
                <Delta tile={t} />
                <span className="landing-row-count" title="Reports in this period">{fmtCompact(t.total)}</span>
              </li>
            );
          })}
        </ol>
      )}
    </article>
  );
}

function Boards() {
  const [range, setRange] = useState<BoardRange>('1y');
  const long = RANGES.find((r) => r.value === range)?.long ?? range;

  return (
    <section className="landing-trends" aria-labelledby="landing-trends-title">
      <header className="landing-trends-head">
        <div>
          <h2 id="landing-trends-title" className="landing-section-title">What’s moving</h2>
          <p className="landing-section-sub">
            The most-reported entities of the last {long}, ranked by change against the {long} before.
          </p>
        </div>
        <div className="landing-trends-controls">
          <div className="landing-seg" role="radiogroup" aria-label="Time range">
            {RANGES.map((r) => (
              <button
                key={r.value}
                type="button"
                role="radio"
                aria-checked={range === r.value}
                className="landing-seg-btn"
                onClick={() => setRange(r.value)}
              >
                {r.label}
              </button>
            ))}
          </div>
          <Link to="/trends" className="landing-textlink">
            All trends <span aria-hidden="true">→</span>
          </Link>
        </div>
      </header>
      <div className="landing-boards">
        <RisingBoard range={range} dim="family" title="Rising families" />
        <RisingBoard range={range} dim="actor" title="Rising actors" />
        <CountryBoard range={range} />
      </div>
    </section>
  );
}

// ── Page ────────────────────────────────────────────────────────────────

type CoreState = 'loading' | 'ready' | 'missing' | 'error';

export default function LandingPage() {
  const coreQ = useQuery({
    queryKey: ['atlas', 'core'],
    queryFn: ({ signal }) => fetchAtlasCore(signal),
    staleTime: 30 * 60_000,
    refetchOnWindowFocus: false,
  });
  const core = coreQ.data;
  const coreState: CoreState = coreQ.isLoading
    ? 'loading'
    : coreQ.isError
      ? 'error'
      : core && core.entities.length > 0
        ? 'ready'
        : 'missing';

  // Any pulse query carries the snapshot's built_at; this one is shared with
  // the family board, so it costs no extra request.
  const pulse = usePulse('1y', 'family');

  return (
    <div className="landing-page">
      <div className="landing-inner">
        <Hero core={core} coreState={coreState} />
        <StatTiles core={core} coreState={coreState} builtAt={pulse.data?.built_at ?? null} />
        <Boards />
      </div>
    </div>
  );
}

// ── formatting ──────────────────────────────────────────────────────────

function fmt(n: number): string {
  return n.toLocaleString('en-US');
}

function fmtCompact(n: number): string {
  if (n < 10_000) return fmt(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 100_000 ? 1 : 0).replace(/\.0$/, '')}k`;
  return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
}

function relTime(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const s = (Date.now() - t) / 1000;
  if (s < 90) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 36 * 3600) return `${Math.round(s / 3600)} h ago`;
  if (s < 14 * 86400) return `${Math.round(s / 86400)} days ago`;
  return new Date(t).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
